/* eslint-disable no-console */
import {Typography} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import React from 'react';
import {DataContainer} from './styles';
import {useHostName, usePersonalData} from 'presentation/hooks';
import {phoneHandler} from 'presentation/utils/phoneHandler';
import {formatAddress, formatText} from 'presentation/utils';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {translations} from 'presentation/translations-new';
import {RegistrationDataItem} from './components/RegistrationDataItem';
import {emailHandler} from 'presentation/utils/emailHandler';
import {RegistrationDataDrawerProps, RegistrationDataItemProps} from './types';
import {useNavigate} from 'react-router-dom';
import {ProfileEnum} from 'presentation/constants';
import {usePostConfirmRegistrationDataMutation} from '../../query/usePostConfirmRegistrationDataMutation';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {ModalError} from 'presentation/components/ModalError';

const {loading, cellphone, email, address, bankDetails} = translations['pt-br'].commons;
const {title, description, firstButtonTitle, secondButtonTitle, accountDataError, agency, account} =
	translations['pt-br'].privateFlow.registrationData.drawer;
const {
	title: modalErrorTitle,
	subtitle,
	description: modalErrorDescription,
	tryAgainBtn,
	backAction,
} = translations['pt-br'].others.genericErrorModal;

const tutorialAndTooltipZIndexPlus1 = 10_101;

export function RegistrationDataDrawer({open, onClose}: RegistrationDataDrawerProps) {
	const navigate = useNavigate();
	const {data: bankData, isLoading, isError} = useGetBeneficiaryBankDataQuery();

	const {currentPhone, currentEmail, userLocation} = usePersonalData();

	const {
		mutateAsync: confirmRegistrationData,
		isPending: isConfirming,
		isError: isConfirmError,
		reset: resetConfirmError,
	} = usePostConfirmRegistrationDataMutation();
	const {
		dataHostNameState: {productCode},
		isGndiSaoPaulo,
	} = useHostName();
	const {accesses, isHolder, odontoOnly, canChangeAddress} = useAccountInformation();
	const showBankData = accesses.refund && isHolder && !odontoOnly && isGndiSaoPaulo;

	const addressData = formatAddress({
		address: userLocation?.nmRua,
		number: userLocation?.nuEndereco,
		zipCode: userLocation?.nuCep,
		city: userLocation?.nmCidade,
		state: userLocation?.nmUf,
		district: userLocation?.nmBairro,
		complement: userLocation?.dsComplemento,
	});

	const getBankDescription = (): string => {
		if (isLoading) return loading;

		if (isError) return accountDataError;

		if (!bankData) return '-';

		const accountNumber = `${bankData?.account ?? ''}${
			bankData?.accountDigit ? `-${bankData.accountDigit}` : ''
		}`;

		return `${formatText(bankData?.bank?.description)}, ${agency} ${bankData?.agency}, ${account} ${accountNumber}`;
	};

	const handleConfirm = async () => {
		const cdEmpresaPlano = productCode?.[0];

		if (cdEmpresaPlano !== undefined) {
			try {
				await confirmRegistrationData({cdEmpresaPlano});
				onClose();
			} catch (error) {
				console.error('Erro ao confirmar dados cadastrais:', error);
			}
		}
	};

	const data: RegistrationDataItemProps[] = [
		{
			iconName: 'phone-android-filled',
			title: cellphone,
			data: phoneHandler(currentPhone).toString(),
		},
		{
			iconName: 'mail-filled',
			title: email,
			data: emailHandler(currentEmail).toString(),
		},
		{
			iconName: 'map',
			title: address,
			data: addressData,
			hidden: !canChangeAddress,
		},
		{
			iconName: 'account-balance-default',
			title: bankDetails,
			data: getBankDescription(),
			hidden: !showBankData,
		},
	];

	return (
		<>
			<Drawer.Root
				open={open}
				onClose={onClose}
				onOpen={() => null}
				customMaxHeight={587}
				sx={{zIndex: tutorialAndTooltipZIndexPlus1}}>
				<Drawer.Header title={title} onClose={onClose} />
				<Drawer.Content>
					<Typography variant="body1" sx={{mt: 3, mb: 3}}>
						{description}
					</Typography>
					<DataContainer>
						{data.map(
							(item, index) =>
								!item.hidden && (
									<RegistrationDataItem
										key={index}
										iconName={item.iconName}
										title={item.title}
										data={item.data}
									/>
								),
						)}
					</DataContainer>
				</Drawer.Content>
				<Drawer.Footer
					firstButtonTitle={firstButtonTitle}
					secondButtonTitle={secondButtonTitle}
					firstButtonOnClick={() => navigate(ProfileEnum.PROFILE)}
					secondButtonOnClick={handleConfirm}
					secondButtonIsLoading={isConfirming}
					secondButtonDisabled={isConfirming}
					firstButtonDisabled={isConfirming}
					canStacks
				/>
			</Drawer.Root>

			{isConfirmError ? (
				<ModalError
					open={isConfirmError}
					onClose={resetConfirmError}
					headerTitle={modalErrorTitle}
					title={subtitle}
					isFullScreen={false}
					maxWidth={420}
					description={modalErrorDescription}
					firstButton={{
						title: tryAgainBtn,
						handleClick: () => {
							resetConfirmError();
							handleConfirm();
						},
						variant: 'contained',
					}}
					secondButton={{
						title: backAction,
						handleClick: () => {
							resetConfirmError();
							onClose();
						},
						variant: 'outlined',
					}}
				/>
			) : null}
		</>
	);
}
